/* =====================================================
   NAVANIKA — Main JavaScript
   Navigation, Cart, Animations, Interactions
   ===================================================== */

let scrollRevealObserver;

document.addEventListener('DOMContentLoaded', () => {
  initNavigation();
  initSearch();
  initWishlistCustomButtons();
  initWishlistAppPage();
  initProductRecommendations();
  initCollectionToolbar();
  initScrollReveal();
  initCart();
  initNewsletterForm();
  initContactForm();
  initScrollTop();
  updateVariantFromSelectors();
  forceWishlistPdpHeartColor();
  fixUnknownColorSwatches();
});

document.addEventListener('shopify:section:load', (event) => {
  initScrollReveal(event.target);
  initWishlistCustomButtons(event.target);
});

/* ---------- Navigation ---------- */
function initNavigation() {
  const navbar = document.getElementById('navbar');
  const toggle = document.getElementById('nav-toggle');
  const mobileNav = document.getElementById('mobile-nav');

  // Scroll effect
  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const curr = window.scrollY;
    if (curr > 60) {
      navbar.classList.add('scrolled');
    } else {
      navbar.classList.remove('scrolled');
    }
    lastScroll = curr;
  }, { passive: true });

  // Mobile toggle
  if (toggle && mobileNav) {
    toggle.addEventListener('click', () => {
      toggle.classList.toggle('active');
      mobileNav.classList.toggle('open');
      document.body.classList.toggle('menu-open');
      document.body.style.overflow = mobileNav.classList.contains('open') ? 'hidden' : '';
    });
  }

  // Mobile quick-action wishlist button proxies to existing Wishlist Hero header trigger.
  const mobileWishlistTrigger = document.getElementById('mobile-wishlist-trigger');
  if (mobileWishlistTrigger) {
    const closeMobileDrawer = () => {
      if (toggle && mobileNav && mobileNav.classList.contains('open')) {
        toggle.classList.remove('active');
        mobileNav.classList.remove('open');
        document.body.classList.remove('menu-open');
        document.body.style.overflow = '';
      }
    };
    const getMobileDrawerCloseDelay = () => {
      if (!mobileNav) return 0;
      const style = window.getComputedStyle(mobileNav);
      const durationToken = (style.transitionDuration || '0s').split(',')[0].trim();
      if (durationToken.endsWith('ms')) return parseFloat(durationToken) + 40;
      if (durationToken.endsWith('s')) return (parseFloat(durationToken) * 1000) + 40;
      return 440;
    };

    const getWishlistTrigger = () => {
      const selectors = [
        '.nav-app-block .wishlisthero-header-block',
        '.nav-app-block .wishlist-hero-header-icon',
        '.nav-app-block [data-wlh-click]',
        '.nav-app-block [role="button"]',
        '.nav-app-block button',
        '.nav-app-block a'
      ];
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) return element;
      }
      return null;
    };
    const openViaHash = () => {
      // Desktop flow lands on this hash; keeping parity improves app compatibility.
      if (window.location.hash === '#hero-wishlist') {
        history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }
      window.location.hash = 'hero-wishlist';
    };

    const clickWishlistTarget = () => {
      const target = getWishlistTrigger();
      if (!target) return false;
      target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      if (typeof target.click === 'function') target.click();
      return true;
    };

    mobileWishlistTrigger.addEventListener('click', (e) => {
      e.preventDefault();

      const openWishlistFlow = () => {
        openViaHash();
        // Wishlist app blocks can initialize late; retry briefly before giving up.
        if (clickWishlistTarget()) return;
        setTimeout(() => { clickWishlistTarget(); }, 180);
        setTimeout(() => { clickWishlistTarget(); }, 700);
      };

      if (mobileNav && mobileNav.classList.contains('open')) {
        closeMobileDrawer();
        setTimeout(openWishlistFlow, getMobileDrawerCloseDelay());
        return;
      }

      openWishlistFlow();
    });
  }
}

/* ---------- Search Overlay ---------- */
function initSearch() {
  // Search overlay HTML is now rendered by Liquid section, no injection needed

  // Ensure recommendation fallback text if Liquid rendered no products
  populateSearchRecommendations();

  // Event Listeners
  const searchIcon = document.getElementById('search-icon');
  const overlay = document.getElementById('search-overlay');
  const closeBtn = document.getElementById('search-close');
  const searchForm = document.getElementById('search-form');
  if (!searchIcon || !overlay || !searchForm) return;
  if (overlay.dataset.searchInitialized === 'true') return;
  overlay.dataset.searchInitialized = 'true';

  const searchInput = overlay.querySelector('.search-input');
  if (!searchInput) return;
  const recommendationsTitle = overlay.querySelector('#search-rec-title');
  const recommendationsGrid = overlay.querySelector('#search-rec-grid');
  const defaultRecommendationsMarkup = recommendationsGrid ? recommendationsGrid.innerHTML : '';
  const defaultRecommendationsTitle = recommendationsTitle
    ? (recommendationsTitle.dataset.defaultTitle || recommendationsTitle.textContent.trim() || 'Recommended For You')
    : 'Recommended For You';
  const predictiveLimit = searchForm.dataset.predictiveLimit || '6';
  let predictiveSearchController = null;

  const debounce = (fn, wait) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), wait);
    };
  };

  const restoreDefaultRecommendations = () => {
    if (predictiveSearchController) {
      predictiveSearchController.abort();
      predictiveSearchController = null;
    }
    if (recommendationsTitle) {
      recommendationsTitle.textContent = defaultRecommendationsTitle;
    }
    if (recommendationsGrid) {
      recommendationsGrid.classList.remove('is-loading');
      recommendationsGrid.innerHTML = defaultRecommendationsMarkup;
      populateSearchRecommendations();
    }
  };

  const getPredictiveSearchUrl = (query) => {
    const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    const basePath = `${shopifyRoot}search/suggest`;
    const predictiveSearchUrl = new URL(basePath, window.location.origin);

    predictiveSearchUrl.searchParams.set('q', query);
    predictiveSearchUrl.searchParams.set('resources[type]', 'product');
    predictiveSearchUrl.searchParams.set('resources[limit]', predictiveLimit);
    predictiveSearchUrl.searchParams.set('resources[options][prefix]', 'last');
    predictiveSearchUrl.searchParams.set('resources[options][unavailable_products]', 'hide');
    predictiveSearchUrl.searchParams.set('section_id', 'predictive-search');

    const currentParams = new URLSearchParams(window.location.search);
    ['preview_theme_id', '_fd', 'pb', '_ab'].forEach((key) => {
      if (currentParams.has(key) && !predictiveSearchUrl.searchParams.has(key)) {
        predictiveSearchUrl.searchParams.set(key, currentParams.get(key));
      }
    });

    return predictiveSearchUrl;
  };

  const renderPredictiveResults = (html) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const predictiveSection = parsed.querySelector('#shopify-section-predictive-search');
    if (!predictiveSection || !recommendationsGrid) {
      restoreDefaultRecommendations();
      return;
    }

    const predictiveTitle = predictiveSection.querySelector('[data-predictive-results-title]');
    const predictiveGrid = predictiveSection.querySelector('[data-predictive-results-grid]');

    if (recommendationsTitle && predictiveTitle) {
      recommendationsTitle.textContent = predictiveTitle.textContent.trim();
    }

    recommendationsGrid.innerHTML = predictiveGrid ? predictiveGrid.innerHTML : '';
    populateSearchRecommendations();
  };

  const requestPredictiveResults = (query) => {
    if (!query || !recommendationsGrid) {
      restoreDefaultRecommendations();
      return;
    }

    if (predictiveSearchController) {
      predictiveSearchController.abort();
    }

    predictiveSearchController = new AbortController();
    recommendationsGrid.classList.add('is-loading');

    fetch(getPredictiveSearchUrl(query).toString(), {
      credentials: 'same-origin',
      signal: predictiveSearchController.signal
    })
      .then(async (response) => {
        const text = await response.text();
        if (!response.ok) {
          throw new Error(`Predictive search failed (${response.status})`);
        }
        return text;
      })
      .then((html) => {
        if (searchInput.value.trim() !== query) return;
        renderPredictiveResults(html);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Predictive search error:', error);
        restoreDefaultRecommendations();
      })
      .finally(() => {
        if (searchInput.value.trim() === query && recommendationsGrid) {
          recommendationsGrid.classList.remove('is-loading');
        }
      });
  };

  const debouncedPredictiveSearch = debounce(() => {
    const query = searchInput.value.trim();
    if (!query.length) {
      restoreDefaultRecommendations();
      return;
    }
    requestPredictiveResults(query);
  }, 250);

  // Open
  searchIcon.addEventListener('click', (e) => {
    e.preventDefault();
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    setTimeout(() => searchInput.focus(), 100);
  });

  // Close function
  const closeSearch = () => {
    overlay.classList.remove('active');
    document.body.style.overflow = document.body.classList.contains('menu-open') ? 'hidden' : '';
  };

  if (closeBtn) {
    closeBtn.addEventListener('click', closeSearch);
    closeBtn.addEventListener('pointerup', closeSearch);
    closeBtn.addEventListener('touchend', closeSearch, { passive: true });
  }

  // Close on background click (only if clicking outside search-scroll-area)
  overlay.addEventListener('click', (e) => {
    if (e.target.closest && e.target.closest('#search-close')) {
      closeSearch();
      return;
    }
    if (e.target === overlay) closeSearch();
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('active')) {
      closeSearch();
    }
  });

  // Handle Trending Tags
  const tags = overlay.querySelectorAll('.search-tag');
  tags.forEach(tag => {
    tag.addEventListener('click', () => {
      searchInput.value = tag.textContent;
      searchInput.focus();
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });

  if (searchInput) {
    searchInput.addEventListener('input', debouncedPredictiveSearch);
  }

  // Handle Submit
  searchForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const query = searchInput.value.trim();
    if (query) {
      closeSearch();
      // Build a robust search URL that also works in Shopify Theme Editor preview.
      const absoluteSearchBase = searchForm.dataset.searchUrl || searchForm.getAttribute('action') || '/search';
      const searchUrl = new URL(absoluteSearchBase, window.location.origin);

      // Preserve preview query params so search stays within the edited theme context.
      const currentParams = new URLSearchParams(window.location.search);
      ['preview_theme_id', '_fd', 'pb', '_ab'].forEach((key) => {
        if (currentParams.has(key) && !searchUrl.searchParams.has(key)) {
          searchUrl.searchParams.set(key, currentParams.get(key));
        }
      });

      searchUrl.searchParams.set('q', query);
      if (!searchUrl.searchParams.has('type')) {
        searchUrl.searchParams.set('type', 'product');
      }
      if (!searchUrl.searchParams.has('options[prefix]')) {
        searchUrl.searchParams.set('options[prefix]', 'last');
      }

      window.location.assign(searchUrl.toString());
    }
  });
}

function populateSearchRecommendations() {
  const grid = document.getElementById('search-rec-grid');
  if (!grid) return;
  if (grid.children.length === 0) {
    grid.innerHTML = '<p style="color:rgba(255,255,255,.4);grid-column:1/-1;text-align:center">No products to show yet</p>';
  }
}

/* ---------- Wishlist Hero Custom Buttons ---------- */
function initWishlistCustomButtons(root = document) {
  const scope = root instanceof Element ? root : document;
  const buttons = scope.querySelectorAll('.wishlist-hero-custom-button');
  if (!buttons.length) return;

  const dispatchWishlistInit = (button) => {
    if (!button || !button.isConnected) return;
    if (button.querySelector('svg, button, a, i')) return;
    document.dispatchEvent(new CustomEvent('wishlist-hero-add-to-custom-element', {
      detail: button
    }));
  };

  buttons.forEach((button) => {
    dispatchWishlistInit(button);

    if (button.dataset.wlhInitScheduled === 'true') return;
    button.dataset.wlhInitScheduled = 'true';

    [300, 1200, 2500, 5000].forEach((delay) => {
      setTimeout(() => dispatchWishlistInit(button), delay);
    });
  });
}

/* ---------- Wishlist App Page ---------- */
function initWishlistAppPage() {
  if (!document.body.classList.contains('wishlist-app-page')) return;

  const main = document.querySelector('main');
  if (!main) return;

  const wishlistRootSelectors = [
    '[class*="wishlisthero"]',
    '[id*="wishlisthero"]',
    '[class*="wishlist-hero"]',
    '[id*="wishlist-hero"]',
    '[class*="wlh"]'
  ];
  const actionSelectors = [
    'a[href^="mailto:"]',
    'a[class*="share"]',
    'button[class*="share"]',
    'a[class*="email"]',
    'button[class*="email"]'
  ].join(', ');
  let rafId = null;

  const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const isAddToCartAction = (element) => {
    const text = normalizeText(element.textContent || element.getAttribute('aria-label'));
    return text.includes('add to cart') || text.includes('add to bag');
  };
  const getProductCards = (container) => {
    const actionCards = Array.from(container.querySelectorAll('button, a'))
      .filter((element) => isAddToCartAction(element))
      .map((element) => element.closest('[class*="card"]'))
      .filter(Boolean);
    if (actionCards.length) return Array.from(new Set(actionCards));

    const candidates = Array.from(container.querySelectorAll('[class*="card"]')).filter((element) => {
      return element.querySelector('img') || isAddToCartAction(element);
    });

    return candidates.filter((element) => {
      return !candidates.some((other) => other !== element && other.contains(element));
    });
  };
  const getCardItem = (card) => {
    const gridItem = card.closest('.MuiGrid-item, [class*="Grid-item"], [class*="grid-item"]');
    return gridItem && gridItem !== card ? gridItem : card;
  };
  const findAddToCartAction = (card) => {
    const actionable = Array.from(card.querySelectorAll('button, a'));
    return actionable.find((element) => isAddToCartAction(element)) || null;
  };

  const getWishlistRoot = () => {
    const candidates = [];

    for (const selector of wishlistRootSelectors) {
      candidates.push(...main.querySelectorAll(selector));
    }

    const uniqueCandidates = Array.from(new Set(candidates));
    const scopedCandidate = uniqueCandidates.find((element) => {
      return element.querySelector('h1, h2, h3, [class*="title"]');
    });
    if (scopedCandidate) return scopedCandidate;

    const actionCandidate = uniqueCandidates.find((element) => {
      return element.querySelector(actionSelectors);
    });
    if (actionCandidate) return actionCandidate;

    uniqueCandidates.sort((left, right) => right.querySelectorAll('*').length - left.querySelectorAll('*').length);
    if (uniqueCandidates[0]) {
      return uniqueCandidates[0];
    }

    return null;
  };

  const findHeaderContainer = (scope, title, actionElements, emptyMessage) => {
    if (!title || !actionElements.length) return null;

    let current = title.parentElement;
    while (current && current !== scope) {
      const containsAction = actionElements.some((element) => current.contains(element));
      const containsCard = current.querySelector('[class*="card"]');
      const containsEmpty = emptyMessage ? current.contains(emptyMessage) : false;

      if (containsAction && !containsCard && !containsEmpty) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  };

  const findEmptyMessage = (scope) => {
    const candidates = Array.from(scope.querySelectorAll('p, span, div'));
    return candidates.find((element) => {
      const text = normalizeText(element.textContent);
      return text.includes('there are no items in this wishlist') && element.children.length === 0;
    }) || null;
  };

  const enhanceWishlist = () => {
    const scope = getWishlistRoot();
    if (!scope) return;

    const shell = scope.closest('main > *:not(script):not(style)') || scope;
    const cards = getProductCards(shell);
    const emptyMessage = findEmptyMessage(scope);
    const titleCandidates = Array.from(scope.querySelectorAll('h1, h2, h3, [class*="title"]'));
    const title = titleCandidates.find((element) => normalizeText(element.textContent).includes('wishlist')) || titleCandidates[0] || null;
    const actionElements = Array.from(scope.querySelectorAll(actionSelectors)).filter((element) => {
      const text = normalizeText(element.textContent || element.getAttribute('aria-label'));
      return Boolean(text);
    });
    const headerContainer = findHeaderContainer(scope, title, actionElements, emptyMessage);
    const gridContainers = Array.from(scope.querySelectorAll('[class*="grid"], [class*="list"]')).filter((element) => {
      return element.querySelector('[class*="card"]');
    });
    const cardItems = Array.from(new Set(cards.map((card) => getCardItem(card)).filter(Boolean)));
    const cardGridContainers = Array.from(new Set(cardItems.map((item) => item.parentElement).filter(Boolean)));
    const isEmpty = Boolean(emptyMessage) && cards.length === 0;

    shell.classList.add('wishlist-theme-shell');
    scope.classList.add('wishlist-theme-root');

    if (title) title.classList.add('wishlist-theme-title');
    if (headerContainer) headerContainer.classList.add('wishlist-theme-header');
    if (emptyMessage) emptyMessage.classList.add('wishlist-theme-empty-message');
    gridContainers.forEach((container) => container.classList.add('wishlist-theme-grid'));
    cardGridContainers.forEach((container) => container.classList.add('wishlist-theme-card-grid'));
    cardItems.forEach((item) => item.classList.add('wishlist-theme-card-item'));
    cards.forEach((card) => {
      card.classList.add('wishlist-theme-card');

      const image = card.querySelector('img');
      if (image) {
        image.classList.add('wishlist-theme-card-image');
        const media = image.closest('a, figure, [class*="media"], [class*="image"], div');
        if (media && media !== card) {
          media.classList.add('wishlist-theme-card-media');
        }
      }

      const addToCart = findAddToCartAction(card);
      if (addToCart) {
        addToCart.classList.add('wishlist-theme-card-cta');
        const footer = addToCart.closest('[class*="action"], [class*="footer"], [class*="button"], [class*="btn"]') || addToCart.parentElement;
        if (footer && footer !== card) {
          footer.classList.add('wishlist-theme-card-footer');
        }
      }
    });

    document.body.classList.toggle('wishlist-is-empty', isEmpty);
    document.body.classList.toggle('wishlist-has-items', cards.length > 0);
    shell.classList.toggle('wishlist-theme-shell--empty', isEmpty);
  };

  const requestEnhancement = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(enhanceWishlist);
  };

  requestEnhancement();

  if (main.dataset.wishlistObserverInitialized === 'true') return;
  main.dataset.wishlistObserverInitialized = 'true';

  const observer = new MutationObserver(requestEnhancement);
  observer.observe(main, { childList: true, subtree: true });

  setTimeout(requestEnhancement, 250);
  setTimeout(requestEnhancement, 1000);
}

/* ---------- Product Recommendations ---------- */
function initProductRecommendations() {
  const placeholder = document.querySelector('[data-related-products-url]');
  if (!placeholder) return;

  let requestUrl;
  try {
    requestUrl = new URL(placeholder.dataset.relatedProductsUrl, window.location.origin);
  } catch (error) {
    console.error('Invalid recommendations URL:', error);
    placeholder.remove();
    return;
  }

  // Keep Theme Editor preview params so section rendering resolves against the preview theme.
  const currentParams = new URLSearchParams(window.location.search);
  ['preview_theme_id', '_fd', 'pb', '_ab'].forEach((key) => {
    if (currentParams.has(key) && !requestUrl.searchParams.has(key)) {
      requestUrl.searchParams.set(key, currentParams.get(key));
    }
  });

  fetch(requestUrl.toString(), { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to load recommendations (${response.status})`);
      return response.text();
    })
    .then((html) => {
      const parsed = document.createElement('div');
      parsed.innerHTML = html;

      const mounted = parsed.querySelector('[data-related-products-mounted="true"]');
      if (!mounted) {
        placeholder.remove();
        return;
      }

      placeholder.replaceWith(mounted);
      initScrollReveal();
      initWishlistCustomButtons(mounted);
    })
    .catch((error) => {
      console.error('Product recommendations error:', error);
      placeholder.remove();
    });
}

/* ---------- Scroll Reveal ---------- */
function initScrollReveal(root = document) {
  root = root instanceof Element ? root : document;
  const elements = root.querySelectorAll('.reveal, .reveal-left, .reveal-right, .stagger-children');
  if (!elements.length) return;

  if (!('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('visible'));
    return;
  }

  if (!scrollRevealObserver) {
    scrollRevealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          entry.target.dataset.revealBound = 'true';
          scrollRevealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: '0px 0px -20px 0px' });
  }

  elements.forEach((el) => {
    if (el.classList.contains('visible') || el.dataset.revealBound === 'true') return;
    el.dataset.revealBound = 'true';
    scrollRevealObserver.observe(el);
  });
}

/* ---------- Scroll to Top ---------- */
function initScrollTop() {
  // Inject HTML
  const btn = document.createElement('button');
  btn.id = 'scroll-top';
  btn.ariaLabel = 'Scroll to Top';
  btn.innerHTML = '↑';
  document.body.appendChild(btn);

  // Scroll Logic
  window.addEventListener('scroll', () => {
    if (window.scrollY > 500) {
      btn.classList.add('visible');
    } else {
      btn.classList.remove('visible');
    }
  }, { passive: true });

  // Click Logic
  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

/* ---------- PDP Wishlist Icon Color ---------- */
function forceWishlistPdpHeartColor() {
  const slot = document.getElementById('wishlist-hero-product-page-button');
  if (!slot) return;

  const royal = getComputedStyle(document.documentElement).getPropertyValue('--royal').trim() || '#0A2472';
  const ctaContainer = slot.closest('.product-ctas') || slot.parentElement || document;

  const applyColor = () => {
    const scope = ctaContainer || document;
    const iconRoots = scope.querySelectorAll(
      '#wishlist-hero-product-page-button, .wishlisthero-product-page-button-container, .wishlist-hero-pdp-slot, .wishlisthero-floating'
    );

    iconRoots.forEach((root) => {
      root.style.setProperty('color', royal, 'important');
      root.querySelectorAll('svg, path, circle, line, polyline, rect, polygon').forEach((el) => {
        el.style.setProperty('stroke', royal, 'important');
        el.style.setProperty('fill', royal, 'important');
      });
      root.querySelectorAll('i, span, a, button').forEach((el) => {
        el.style.setProperty('color', royal, 'important');
      });
    });
  };

  applyColor();

  // Wishlist Hero can re-render after load; keep color pinned briefly.
  const observer = new MutationObserver(() => applyColor());
  observer.observe(ctaContainer, { childList: true, subtree: true, attributes: true });
  setTimeout(() => observer.disconnect(), 12000);
}

function syncWishlistProductButton(variant) {
  const button = document.getElementById('wishlist-hero-product-page-button');
  if (!button || !variant) return;

  const baseLink = button.dataset.wlhBaseLink || window.location.pathname;
  const baseName = button.dataset.wlhBaseName || '';

  button.dataset.wlhVariantid = String(variant.id);
  button.dataset.wlhPrice = String(Number(variant.price || 0) / 100);
  button.dataset.wlhVariantlistener = 'product-page';

  if (baseLink) {
    const variantUrl = new URL(baseLink, window.location.origin);
    variantUrl.searchParams.set('variant', variant.id);
    button.dataset.wlhLink = variantUrl.toString();
  }

  if (variant.featured_image && variant.featured_image.src) {
    button.dataset.wlhImage = variant.featured_image.src;
  }

  if (variant.name) {
    button.dataset.wlhName = variant.name;
  } else if (variant.public_title && variant.public_title !== 'Default Title' && baseName) {
    button.dataset.wlhName = `${baseName} - ${variant.public_title}`;
  } else if (baseName) {
    button.dataset.wlhName = baseName;
  }
}

/* ---------- Cart (Shopify Cart API) ---------- */

/**
 * Fetch the current cart from Shopify and update all badges.
 * Returns the cart JSON object.
 */
async function fetchCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  return res.json();
}

/** Update every .cart-count badge on the page */
function updateCartBadge(cart) {
  document.querySelectorAll('.cart-count').forEach(el => {
    if (cart.item_count > 0) {
      el.textContent = cart.item_count;
      el.style.display = 'flex';
    } else {
      el.style.display = 'none';
    }
  });
}

/**
 * Add a variant to the Shopify cart via AJAX.
 * @param {number|string} variantId  – The variant ID
 * @param {number}        qty        – Quantity to add (default 1)
 * @returns {Promise<Object>}        – The line item returned by Shopify
 */
async function addToCart(variantId, qty = 1) {
  const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';
  const res = await fetch(`${shopifyRoot}cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      items: [{
        id: Number(variantId),
        quantity: qty
      }]
    })
  });
  const item = await res.json();
  // Refresh badge count
  const cart = await fetchCart();
  updateCartBadge(cart);
  return item;
}

/**
 * Change the quantity of a line item (set to 0 to remove).
 * @param {string} lineKey  – The line item key
 * @param {number} qty      – New quantity
 */
async function changeCartQty(lineKey, qty) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: Math.max(0, qty) })
  });
  const cart = await res.json();
  updateCartBadge(cart);
  return cart;
}

/** Initialise: badge on load + AJAX add-to-cart + cart page AJAX */
function initCart() {
  // -- Badge --
  fetchCart().then(updateCartBadge).catch(() => { });

  // -- AJAX Add to Cart (intercept product form, Dawn-style) --
  const productForm = document.querySelector('form[action*="/cart/add"]');
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      const submitBtn = productForm.querySelector('[type="submit"]');
      if (submitBtn && submitBtn.disabled) return;
      if (productForm.dataset.cartSubmitting === 'true') return;

      const originalText = submitBtn ? submitBtn.textContent : '';
      const defaultText = submitBtn ? submitBtn.dataset.defaultText || originalText || 'Add to Cart' : 'Add to Cart';
      const addedText = submitBtn ? submitBtn.dataset.addedText || 'Added to Cart' : 'Added to Cart';
      productForm.dataset.cartSubmitting = 'true';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.textContent = 'Adding...';
      }

      try {
        const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
        const variantIdInput = productForm.querySelector('#selected-variant-id, input[name="id"]');
        const quantityInput = productForm.querySelector('input[name="quantity"]');
        const sellingPlanInput = productForm.querySelector('[name="selling_plan"]');
        const variantId = variantIdInput ? Number(variantIdInput.value) : NaN;
        const quantity = quantityInput ? Math.max(1, Number(quantityInput.value) || 1) : 1;

        if (!variantId) {
          throw new Error('Please select an available variant before adding to cart.');
        }

        const lineItem = {
          id: variantId,
          quantity
        };

        if (sellingPlanInput && sellingPlanInput.value) {
          lineItem.selling_plan = sellingPlanInput.value;
        }

        const propertyInputs = productForm.querySelectorAll('[name^="properties["]');
        if (propertyInputs.length) {
          const properties = {};
          propertyInputs.forEach((input) => {
            const propertyName = input.name.replace(/^properties\[|\]$/g, '');
            if (!propertyName || !input.value) return;
            properties[propertyName] = input.value;
          });
          if (Object.keys(properties).length) {
            lineItem.properties = properties;
          }
        }

        const payload = {
          items: [lineItem]
        };

        const res = await fetch(`${shopifyRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });

        let responsePayload = null;
        const responseText = await res.text();
        try {
          responsePayload = responseText ? JSON.parse(responseText) : null;
        } catch (_) {
          responsePayload = null;
        }

        if (!res.ok) {
          const message = responsePayload && (responsePayload.description || responsePayload.message)
            ? responsePayload.description || responsePayload.message
            : `Unable to add this product to cart (${res.status})`;
          throw new Error(message);
        }

        const item = responsePayload || {};
        const cart = await fetchCart();
        updateCartBadge(cart);
        showNotification(`${item.title || 'Item'} added to cart!`);
        if (submitBtn) {
          submitBtn.textContent = addedText;
          if (submitBtn._resetTextTimer) clearTimeout(submitBtn._resetTextTimer);
          submitBtn._resetTextTimer = setTimeout(() => {
            if (!submitBtn.disabled) submitBtn.textContent = defaultText;
          }, 1600);
        }
      } catch (err) {
        console.error('Add to cart failed:', err);
        showNotification(err && err.message ? err.message : 'Unable to add this product to cart.');
      } finally {
        productForm.dataset.cartSubmitting = 'false';
        if (submitBtn) {
          const keepDisabled = submitBtn.dataset.keepDisabled === 'true';
          if (!keepDisabled) {
            submitBtn.disabled = false;
            submitBtn.setAttribute('aria-disabled', 'false');
          } else {
            submitBtn.disabled = true;
            submitBtn.setAttribute('aria-disabled', 'true');
            submitBtn.textContent = submitBtn.dataset.disabledText || submitBtn.dataset.soldOutText || defaultText;
          }
        }
      }
    });
  }

  // -- AJAX quantity & remove on cart page --
  document.addEventListener('click', async (e) => {
    const target = e.target.closest('[data-cart-change]');
    if (!target) return;

    const cartContent = document.getElementById('cart-content');
    if (!cartContent || !cartContent.contains(target)) return;

    e.preventDefault();

    const lineKey = target.dataset.lineKey;
    const newQty = Number(target.dataset.cartChange);
    if (!lineKey || Number.isNaN(newQty)) return;

    const cart = await changeCartQty(lineKey, newQty);

    // If cart is now empty, reload to show empty state from Liquid
    if (cart.item_count === 0) {
      window.location.reload();
      return;
    }

    // Re-render cart section via section rendering API
    const sectionRoot = document.querySelector('[data-section-id]');
    if (sectionRoot) {
      const secId = sectionRoot.dataset.sectionId;
      const html = await fetch(`${window.location.pathname}?section_id=${secId}`).then(r => r.text());
      const temp = document.createElement('div');
      temp.innerHTML = html;
      const newContent = temp.querySelector('#cart-content') || temp.querySelector('.cart-layout');
      const currentContent = document.getElementById('cart-content');
      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
    }
  });

  // -- Clean up old localStorage cart data --
  localStorage.removeItem('navanika_cart');
}

/* ---------- Notification Toast ---------- */
function showNotification(message) {
  const existing = document.querySelector('.toast-notify');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'toast-notify';
  toast.innerHTML = `<span>${message}</span>`;
  toast.style.cssText = `
    position:fixed;bottom:2rem;right:2rem;z-index:9999;
    padding:1rem 1.8rem;background:#0A2472;color:#fff;
    font-family:var(--font-body);font-size:.8rem;letter-spacing:.08em;
    box-shadow:0 8px 30px rgba(0,0,0,.2);
    opacity:0;transform:translateY(20px);
    transition:all .4s cubic-bezier(.22,1,.36,1);
  `;
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(20px)';
    setTimeout(() => toast.remove(), 400);
  }, 2800);
}

/* ---------- Product Detail Page Interactions ---------- */
function changeImage(thumb) {
  const main = document.getElementById('gallery-main-img');
  if (!main) return;
  main.src = thumb.dataset.fullSrc || thumb.src;
  if (thumb.dataset.fullAlt) main.alt = thumb.dataset.fullAlt;
  document.querySelectorAll('.product-gallery-thumbs img').forEach(t => t.classList.remove('active'));
  thumb.classList.add('active');
}

/* ---------- Color Swatch Fallback ---------- */
/**
 * Detect colour swatches whose CSS background resolved to transparent
 * (the colour name wasn't in the Liquid map or isn't a valid CSS colour)
 * and generate a deterministic colour from the name so it's always visible.
 */
function fixUnknownColorSwatches() {
  document.querySelectorAll('.color-option').forEach(swatch => {
    const inner = swatch.querySelector('.color-option-inner');
    if (!inner) return;

    // Check if the background actually rendered a colour
    const bg = window.getComputedStyle(inner).backgroundColor;
    // "transparent", "rgba(0, 0, 0, 0)" or empty means the CSS colour was invalid
    if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') return;

    // Generate a deterministic hue from the colour name
    const name = (swatch.dataset.color || '').trim();
    if (!name) return;
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = ((hash % 360) + 360) % 360;
    inner.style.background = `hsl(${hue}, 45%, 55%)`;
    inner.title = inner.title || name;
  });
}

/** Parse variant JSON once from the page */
let _productVariants = null;
function getProductVariants() {
  if (_productVariants) return _productVariants;
  const el = document.getElementById('product-variants-json');
  if (!el) return [];
  try { _productVariants = JSON.parse(el.textContent); } catch { _productVariants = []; }
  return _productVariants;
}

/** Match selected options to a variant and update the hidden id input */
function updateVariantFromSelectors() {
  const variants = getProductVariants();
  if (!variants.length) return;

  const selectedByPosition = {};
  document.querySelectorAll('.color-option.active, .size-option.active').forEach(el => {
    const position = Number(el.dataset.optionPosition);
    if (!position) return;
    selectedByPosition[position] = el.dataset.optionValue || el.textContent.trim();
  });
  document.querySelectorAll('.variant-option-select').forEach(sel => {
    const position = Number(sel.dataset.optionPosition);
    if (!position) return;
    selectedByPosition[position] = sel.value;
  });

  const match = variants.find(variant => {
    for (let i = 1; i <= 3; i += 1) {
      if (selectedByPosition[i] !== undefined && variant[`option${i}`] !== selectedByPosition[i]) {
        return false;
      }
    }
    return true;
  });

  const idInput = document.getElementById('selected-variant-id');
  const addBtn = document.getElementById('add-to-cart-btn');
  const defaultButtonText = addBtn ? addBtn.dataset.defaultText || 'Add to Cart' : 'Add to Cart';
  const soldOutText = addBtn ? addBtn.dataset.soldOutText || 'Sold Out' : 'Sold Out';
  const unavailableText = addBtn ? addBtn.dataset.unavailableText || 'Unavailable' : 'Unavailable';
  if (match) {
    if (idInput) idInput.value = match.id;
    if (addBtn) {
      addBtn.disabled = !match.available;
      addBtn.dataset.keepDisabled = match.available ? 'false' : 'true';
      addBtn.setAttribute('aria-disabled', match.available ? 'false' : 'true');
      addBtn.dataset.disabledText = match.available ? '' : soldOutText;
      addBtn.textContent = match.available ? defaultButtonText : soldOutText;
    }
    syncWishlistProductButton(match);
  } else {
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.dataset.keepDisabled = 'true';
      addBtn.setAttribute('aria-disabled', 'true');
      addBtn.dataset.disabledText = unavailableText;
      addBtn.textContent = unavailableText;
    }
  }
}

function selectSize(el) {
  if (el.classList.contains('disabled')) return;
  document.querySelectorAll('.size-option').forEach(s => s.classList.remove('active'));
  el.classList.add('active');
  const label = document.getElementById('selected-size');
  if (label) label.textContent = el.textContent;
  updateVariantFromSelectors();
}

function selectColor(el) {
  document.querySelectorAll('.color-option').forEach(c => c.classList.remove('active'));
  el.classList.add('active');
  const label = document.getElementById('selected-color-name');
  if (label) label.textContent = el.dataset.color;
  updateVariantFromSelectors();
}

let currentQty = 1;
function changeQty(delta) {
  currentQty = Math.max(1, currentQty + delta);
  const display = document.getElementById('qty-value');
  if (display) display.value = currentQty;
}

/* ---------- Newsletter ---------- */
function initNewsletterForm() {
  // Clean legacy redirect params from non-AJAX submits so page doesn't jump to newsletter hash.
  try {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get('customer_posted') === 'true') {
      currentUrl.searchParams.delete('customer_posted');
      if (currentUrl.hash === '#newsletter-form' || currentUrl.hash === '#about-newsletter-form' || currentUrl.hash === '#contact_form') {
        currentUrl.hash = '';
      }
      history.replaceState(null, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    }
  } catch (_) { }

  const submitNewsletterAjax = async (form) => {
    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    const emailInput = form.querySelector('input[name="contact[email]"], input[type="email"]');
    if (form.dataset.submitting === 'true') return;

    if (emailInput && typeof emailInput.checkValidity === 'function' && !emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }

    const originalText = submitBtn ? submitBtn.textContent : '';
    form.dataset.submitting = 'true';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Subscribing...';
    }

    try {
      const actionAttr = form.getAttribute('action') || window.location.pathname;
      const actionUrl = new URL(actionAttr, window.location.origin);
      actionUrl.hash = '';

      // Keep Theme Editor preview context when submitting from preview.
      const currentParams = new URLSearchParams(window.location.search);
      ['preview_theme_id', '_fd', 'pb', '_ab'].forEach((key) => {
        if (currentParams.has(key) && !actionUrl.searchParams.has(key)) {
          actionUrl.searchParams.set(key, currentParams.get(key));
        }
      });

      const formData = new FormData(form);
      if (!formData.get('contact[tags]')) {
        formData.set('contact[tags]', 'newsletter');
      }

      const response = await fetch(actionUrl.toString(), {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Newsletter request failed (${response.status})`);

      const finalUrl = new URL(response.url, window.location.origin);
      const responseHtml = await response.text();
      const successByRedirect = finalUrl.searchParams.get('customer_posted') === 'true';
      const failureHint = /form[-_ ]?errors?|customer\[email\].*invalid|enter a valid email/i.test(responseHtml);
      if (!successByRedirect && failureHint) {
        throw new Error('Invalid newsletter email');
      }

      form.reset();
      showNotification('Thank you for subscribing! Welcome to the Royal Court.');
    } catch (err) {
      console.error('Newsletter subscribe failed:', err);
      showNotification('Subscription failed. Please try again.');
    } finally {
      form.dataset.submitting = 'false';
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText || 'Subscribe';
      }
    }
  };

  // Delegate in capture phase so native submit never takes over, even if sections re-render.
  document.addEventListener('submit', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLFormElement)) return;
    if (!target.classList.contains('newsletter-form')) return;
    e.preventDefault();
    submitNewsletterAjax(target);
  }, true);
}

/* ---------- Contact Form ---------- */
function initContactForm() {
  // Shopify's contact form is a standard POST that reloads the page.
  // On genuine success, Liquid renders #contact-form-success (form.posted_successfully?).
  // Only surface the toast when that real success marker is present — never on submit.
  const successFlag = document.getElementById('contact-form-success');
  if (successFlag) {
    showNotification(successFlag.dataset.message || 'Thank you! Your message has been sent.');
  }
}

/* ---------- Collection Sort (Server-side) ---------- */
function initCollectionToolbar() {
  const sortSelect = document.getElementById('filter-sort');
  if (!sortSelect) return;

  sortSelect.addEventListener('change', () => {
    const nextSort = sortSelect.value;
    const nextUrl = new URL(window.location.href);
    if (nextSort) {
      nextUrl.searchParams.set('sort_by', nextSort);
    } else {
      nextUrl.searchParams.delete('sort_by');
    }
    nextUrl.searchParams.delete('page');
    window.location.href = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
  });
}
